import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { VEHICLE_TYPES, JOB_CATEGORIES } from "../lib/constants";
import { MapPin, Truck, Boxes, Calendar, Package } from "lucide-react";
import { EmptyStateCTA } from "./EmptyStateCTA";

const ApplyDialog = ({ job, onApplied }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ quote: "", message: "", contact: "" });
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    setSaving(true);
    try {
      await api.post(`/transport-jobs/${job.job_id}/apply`, { quote: form.quote ? parseFloat(form.quote) : null, message: form.message, contact: form.contact });
      toast.success("Quote submitted");
      setOpen(false);
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-primary hover:bg-primary/90 w-full mt-4" data-testid={`apply-job-${job.job_id}`}>Apply with quote</Button>
      </DialogTrigger>
      <DialogContent className="rounded-none">
        <DialogHeader><DialogTitle className="font-display">Apply: {job.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-caps">Your quote (£)</Label><Input type="number" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="rounded-none mt-1 h-10" data-testid="apply-quote" /></div>
          <div><Label className="label-caps">Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="rounded-none mt-1" placeholder="Availability, vehicle, ETA…" data-testid="apply-message" /></div>
          <div><Label className="label-caps">Contact (phone / WhatsApp)</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="rounded-none mt-1 h-10" data-testid="apply-contact" /></div>
        </div>
        <DialogFooter><Button onClick={apply} disabled={saving} className="rounded-none bg-primary hover:bg-primary/90" data-testid="submit-application-btn">{saving ? "Submitting…" : "Submit quote"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const JobCard = ({ job, user, onApplied }) => {
  let action = null;
  if (!user) action = <Link to="/register" className="block mt-4"><Button variant="outline" className="rounded-none border-foreground w-full">Register as Transporter to apply</Button></Link>;
  else if (user.role === "driver" && user.status !== "approved") action = <Button disabled className="rounded-none w-full mt-4">Pending admin approval</Button>;
  else if (user.role === "driver" && job.already_applied) action = <Button disabled className="rounded-none w-full mt-4" data-testid={`applied-${job.job_id}`}>Quote submitted</Button>;
  else if (user.role === "driver") action = <ApplyDialog job={job} onApplied={onApplied} />;

  return (
    <div className="border border-border p-5 bg-white" data-testid={`board-job-${job.job_id}`}>
      <div className="flex items-center justify-between mb-2">
        <Truck className="h-5 w-5 text-primary" />
        <Badge className="rounded-none bg-primary text-white text-[10px] uppercase">{job.status}</Badge>
      </div>
      <h3 className="font-display font-bold text-lg tracking-tight">{job.title}</h3>
      {job.job_category && <span className="label-caps text-primary">{job.job_category}</span>}
      <div className="space-y-1 mt-3 text-sm">
        <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-primary" /> {job.pickup_location} → {job.delivery_location}</div>
        {job.truck_type && <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-muted-foreground" /> {job.truck_type}{job.loading_help ? " · loading help" : ""}</div>}
        {(job.goods_type || job.weight || job.pallets) && <div className="flex items-center gap-2"><Boxes className="h-3.5 w-3.5 text-muted-foreground" /> {[job.goods_type, job.weight, job.pallets && `${job.pallets} pallets`].filter(Boolean).join(" · ")}</div>}
        {(job.pickup_datetime || job.delivery_deadline) && <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-muted-foreground" /> {[job.pickup_datetime, job.delivery_deadline].filter(Boolean).join(" → ")}</div>}
        <div className="flex items-center gap-2 font-medium"><Package className="h-3.5 w-3.5 text-muted-foreground" /> {job.request_quote ? "Request a quote" : job.price ? `£${job.price}` : "—"}</div>
      </div>
      {job.description && <p className="text-sm text-muted-foreground mt-2">{job.description}</p>}
      <p className="text-xs text-muted-foreground mt-2">Posted by {job.poster_name}</p>
      {action}
    </div>
  );
};

export const JobsBoard = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ pickup: "", delivery: "", truck_type: "all", job_category: "all" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.pickup) params.pickup = filters.pickup;
      if (filters.delivery) params.delivery = filters.delivery;
      if (filters.truck_type !== "all") params.truck_type = filters.truck_type;
      if (filters.job_category !== "all") params.job_category = filters.job_category;
      const r = await api.get("/transport-jobs", { params });
      setJobs(r.data);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Input value={filters.pickup} onChange={(e) => setFilters({ ...filters, pickup: e.target.value })} placeholder="Pickup location" className="rounded-none h-10" data-testid="board-filter-pickup" />
        <Input value={filters.delivery} onChange={(e) => setFilters({ ...filters, delivery: e.target.value })} placeholder="Delivery location" className="rounded-none h-10" data-testid="board-filter-delivery" />
        <Select value={filters.truck_type} onValueChange={(v) => setFilters({ ...filters, truck_type: v })}>
          <SelectTrigger className="rounded-none h-10" data-testid="board-filter-truck"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All vehicle types</SelectItem>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.job_category} onValueChange={(v) => setFilters({ ...filters, job_category: v })}>
          <SelectTrigger className="rounded-none h-10" data-testid="board-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All job types</SelectItem>{JOB_CATEGORIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground" data-testid="board-job-count">{jobs.length} open jobs</span>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-56 bg-muted animate-pulse border border-border" />)}</div>
      ) : jobs.length === 0 ? (
        <EmptyStateCTA
          testid="board-empty"
          icon={Truck}
          title="No open transport jobs yet. Companies can post transport jobs for Sprinter, van, truck and container transport."
          subtitle="Approved suppliers and companies post jobs and receive quotes from verified transporters across Europe."
          actions={[
            { label: "Post Transport Job", to: "/transport", variant: "primary", testid: "board-empty-post-job" },
            { label: "Find a Transporter", to: "/find-transporter", testid: "board-empty-find-transporter" },
            { label: "Register as Transporter / Sprinter Driver", to: "/register", testid: "board-empty-register-driver" },
          ]}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((j) => <JobCard key={j.job_id} job={j} user={user} onApplied={load} />)}
        </div>
      )}
    </div>
  );
};
